import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { ArrowRight, Mail, Lock, Eye, EyeOff } from "lucide-react";
import FloodlightBeam from "../../components/brand/FloodlightBeam";
import { SouburWordmark } from "../../components/brand/SoubourMark";
import { useAuth } from "../../lib/auth";
import { formatApiErrorDetail } from "../../lib/api";

const QUICK_LOGINS = [
    { label: "Platform owner (Joshua)", email: "Joshua.James@soubor-apps.com" },
    { label: "Club admin (Ian @ RLFC)", email: "ian@runcornlinnets.com" },
    { label: "Sponsor (Maria @ Halton Heating)", email: "maria@haltonheating.co.uk" },
];

export default function Login() {
    const [showPw, setShowPw] = useState(false);
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("soubor-dev-2026");
    const [err, setErr] = useState("");
    const [busy, setBusy] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();
    const { login } = useAuth();

    const goHome = (user) => {
        const from = location.state?.from?.pathname;
        // Resolve role-default first so we never land on a route outside the role's allowed area
        // (e.g. platform_owner returning from /club after impersonation, or a back-nav loop).
        const defaultHome =
            user.role === "platform_owner" ? "/platform"
            : user.role === "club_admin" ? "/club"
            : user.role === "sponsor" ? "/sponsor"
            : "/";
        const allowedPrefix = defaultHome === "/" ? null : defaultHome;
        const fromIsAllowed = from && from !== "/login" && allowedPrefix && from.startsWith(allowedPrefix);
        navigate(fromIsAllowed ? from : defaultHome, { replace: true });
    };

    const onSubmit = async (e) => {
        e.preventDefault();
        setErr("");
        setBusy(true);
        try {
            const u = await login(email, password);
            goHome(u);
        } catch (e2) {
            setErr(formatApiErrorDetail(e2.response?.data?.detail) || e2.message);
        } finally {
            setBusy(false);
        }
    };

    return (
        <div data-testid="login-page" className="min-h-screen grid grid-cols-1 lg:grid-cols-12 bg-[var(--sb-page)] text-white">
            <div className="lg:col-span-5 relative flex flex-col min-h-screen">
                <div className="px-6 md:px-12 pt-8">
                    <Link to="/" data-testid="login-home"><SouburWordmark animated /></Link>
                </div>

                <div className="flex-1 flex items-center px-6 md:px-12">
                    <div className="w-full max-w-md mx-auto py-16">
                        <div className="text-xs uppercase tracking-ultra-wide text-zinc-500 mb-4 reveal" data-testid="login-eyebrow">Welcome back</div>
                        <h1 className="font-sport text-4xl md:text-5xl leading-[0.95] uppercase reveal reveal-d1" data-testid="login-title">
                            Sign in to<br />your club.
                        </h1>
                        <p className="text-zinc-400 mt-4 reveal reveal-d2">One workspace for the entire commercial side of your matchday.</p>

                        <form className="mt-10 space-y-5 reveal reveal-d3" onSubmit={onSubmit} data-testid="login-form">
                            <div>
                                <label className="text-xs uppercase tracking-ultra-wide text-zinc-500" htmlFor="email">Email</label>
                                <div className="mt-2 relative border hairline focus-within:border-white/30 transition-colors">
                                    <Mail size={14} className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-500" />
                                    <input id="email" type="email" autoComplete="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                                        placeholder="ian@runcornlinnets.com"
                                        className="w-full bg-transparent pl-11 pr-4 py-3.5 text-sm text-white placeholder:text-zinc-600 outline-none"
                                        data-testid="login-email" />
                                </div>
                            </div>

                            <div>
                                <div className="flex items-center justify-between">
                                    <label className="text-xs uppercase tracking-ultra-wide text-zinc-500" htmlFor="password">Password</label>
                                    <span className="text-[10px] uppercase tracking-ultra-wide text-zinc-600" data-testid="login-dev-hint">dev: soubor-dev-2026</span>
                                </div>
                                <div className="mt-2 relative border hairline focus-within:border-white/30 transition-colors">
                                    <Lock size={14} className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-500" />
                                    <input id="password" type={showPw ? "text" : "password"} autoComplete="current-password" required value={password} onChange={(e) => setPassword(e.target.value)}
                                        placeholder="••••••••"
                                        className="w-full bg-transparent pl-11 pr-11 py-3.5 text-sm text-white placeholder:text-zinc-600 outline-none"
                                        data-testid="login-password" />
                                    <button type="button" onClick={() => setShowPw((s) => !s)} className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white p-1"
                                        data-testid="login-toggle-password" aria-label={showPw ? "Hide password" : "Show password"}>
                                        {showPw ? <EyeOff size={14} /> : <Eye size={14} />}
                                    </button>
                                </div>
                            </div>

                            {err && (
                                <div data-testid="login-error" className="border border-rose-500/40 text-rose-300 text-xs px-3 py-2.5">
                                    {err}
                                </div>
                            )}

                            <button type="submit" disabled={busy}
                                className="w-full inline-flex items-center justify-center gap-2 bg-[var(--sb-amber)] text-black font-semibold tracking-tight px-6 py-3.5 hover:brightness-110 transition-all disabled:opacity-50"
                                data-testid="login-submit">
                                {busy ? "Signing in…" : <>Sign in <ArrowRight size={16} strokeWidth={2.4} /></>}
                            </button>

                            <div className="pt-4">
                                <div className="text-[10px] uppercase tracking-ultra-wide text-zinc-600 mb-2">Quick sign-in (dev)</div>
                                <div className="grid grid-cols-1 gap-2">
                                    {QUICK_LOGINS.map((q) => (
                                        <button
                                            key={q.email}
                                            type="button"
                                            onClick={() => { setEmail(q.email); setPassword("soubor-dev-2026"); }}
                                            className="text-left border hairline px-3 py-2 text-xs hover:bg-white/5 transition-colors"
                                            data-testid={`quick-login-${q.email.split("@")[0]}`}
                                        >
                                            <span className="text-zinc-300">{q.label}</span>
                                            <span className="block text-zinc-600 text-[10px] mt-0.5 font-mono">{q.email}</span>
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </form>
                    </div>
                </div>

                <div className="px-6 md:px-12 pb-8 text-xs text-zinc-600 flex items-center justify-between">
                    <span className="font-editorial italic text-zinc-400">powered by Soubor Applications Ltd</span>
                    <span>© {new Date().getFullYear()} Soubor Applications Ltd.</span>
                </div>
            </div>

            <div className="hidden lg:block lg:col-span-7 relative border-l hairline">
                <div className="absolute inset-0"><FloodlightBeam intensity={1} testId="login-floodlight" /></div>
                <div className="absolute inset-0 z-20 flex flex-col justify-between p-12">
                    <div className="flex items-start justify-between text-xs uppercase tracking-ultra-wide text-white/50">
                        <span>Floodlight on</span>
                        <span className="inline-flex items-center gap-2">
                            <span className="kpi-pip" style={{ background: "var(--sb-amber)" }} /> kick-off 19:45
                        </span>
                    </div>
                    <div className="max-w-xl">
                        <div className="font-editorial text-white/85 text-3xl md:text-4xl leading-[1.05]" data-testid="login-quote">
                            Grassroots clubs run their commercial side on goodwill and gut feel. We built the tool that earns them a second look.
                        </div>
                        <div className="mt-6 text-xs text-zinc-400 uppercase tracking-ultra-wide">
                            Floodlight — sponsor & commercial OS for football clubs.
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
